/**
 * Ticket query: the one filter-and-sort spec the XPM Agent writes when it
 * reinterprets the ticket queue in plain language ("close every overdue
 * Facilities ticket", "show my critical tickets by due date"). The same spec
 * drives three things:
 *
 *   * list_tickets          - answer in chat
 *   * show_tickets_in_grid  - filter and sort the Tickets grid (?ai= in the URL)
 *   * bulk_update_tickets   - pick which tickets a confirmed bulk change hits
 *
 * Same contract as Cost Management's ledger filter (lib/costmgmt/ledgerFilter.ts):
 * the agent writes a spec, `describe` renders it back as a sentence, and the
 * person sees exactly what their words resolved to before anything runs.
 *
 * Rules:
 *   * conditions are ANDed; the values inside one condition are ORed
 *   * an empty condition list matches every ticket the user can see
 *   * every value is resolved against real data (users, customers,
 *     categories) up front. Anything unknown or ambiguous is an ERROR, never
 *     a silent match-all or match-none, so the agent asks instead of guessing
 *     and a misread request can never widen a bulk change.
 *
 * Pure and framework-free, so it is unit-tested and shared by the tools and
 * the grid.
 */
import { TICKET_PRIORITIES, TICKET_STATUSES, type Ticket, type TicketPriority, type TicketStatus } from '../types/ticket'

export const QUERY_FIELDS = [
  'status', 'priority', 'category', 'customer', 'assignee', 'requester', 'createdBy',
  'overdue', 'dueDate', 'createdAt', 'updatedAt', 'text', 'ticketNumber',
] as const
export type QueryField = (typeof QUERY_FIELDS)[number]

export const QUERY_OPS = ['in', 'not', 'before', 'after', 'contains', 'is'] as const
export type QueryOp = (typeof QUERY_OPS)[number]

export const SORT_FIELDS = [
  'ticketNumber', 'title', 'status', 'priority', 'category', 'assignee', 'requester', 'dueDate', 'createdAt', 'updatedAt',
] as const
export type SortField = (typeof SORT_FIELDS)[number]

export interface TicketCondition {
  field: QueryField
  op: QueryOp
  value: string | string[]
}

export interface TicketQuery {
  where: TicketCondition[]
  sort?: { field: SortField; direction: 'asc' | 'desc' }
}

export interface QueryContext {
  users: { id: string; name: string; email?: string }[]
  customers: { id: string; name: string }[]
  // Every category name worth matching: the admin list plus any (retired)
  // name still on a ticket.
  categories: string[]
  currentUserId: string | null
  // YYYY-MM-DD in the user's own timezone. "Overdue" means open with a due
  // date before this day; a ticket due today is not overdue yet.
  today: string
}

export type CompiledQuery =
  | { ok: true; match: (t: Ticket) => boolean; compare: ((a: Ticket, b: Ticket) => number) | null; description: string }
  | { ok: false; error: string }

const OPEN_GROUP = TICKET_STATUSES.filter((s) => s !== 'Resolved' && s !== 'Closed')
const CLOSED_GROUP: TicketStatus[] = ['Resolved', 'Closed']
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function isOpenStatus(status: TicketStatus): boolean {
  return status !== 'Resolved' && status !== 'Closed'
}

function squash(v: string): string {
  return v.replace(/[\s_-]/g, '').toLowerCase()
}

function values(c: TicketCondition): string[] {
  const raw = Array.isArray(c.value) ? c.value : [c.value]
  return raw.map((v) => String(v ?? '').trim()).filter(Boolean)
}

function list(items: string[], joiner = 'or'): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} ${joiner} ${items[items.length - 1]}`
}

function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

// The day part of a stored date or timestamp, in the user's timezone for a
// timestamp (created/updated) and as-is for a date column (due date).
function dayOf(value: string | null): string | null {
  if (!value) return null
  if (DATE_RE.test(value)) return value
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

type Resolved<T> = { ok: true; value: T } | { ok: false; error: string }

function resolveStatuses(raw: string[]): Resolved<{ statuses: TicketStatus[]; label: string }> {
  const out = new Set<TicketStatus>()
  const labels: string[] = []
  for (const v of raw) {
    const k = squash(v)
    if (k === 'open' || k === 'active') { OPEN_GROUP.forEach((s) => out.add(s)); labels.push('open'); continue }
    if (k === 'closed' || k === 'done' || k === 'resolvedorclosed') { CLOSED_GROUP.forEach((s) => out.add(s)); labels.push('resolved or closed'); continue }
    const s = TICKET_STATUSES.find((x) => x.toLowerCase() === k)
    if (!s) return { ok: false, error: `"${v}" is not a status. Use ${TICKET_STATUSES.join(', ')}, or "open" / "closed".` }
    out.add(s)
    labels.push(s)
  }
  return { ok: true, value: { statuses: [...out], label: list(labels) } }
}

function resolvePriorities(raw: string[]): Resolved<TicketPriority[]> {
  const out: TicketPriority[] = []
  for (const v of raw) {
    const p = TICKET_PRIORITIES.find((x) => x.toLowerCase() === squash(v))
    if (!p) return { ok: false, error: `"${v}" is not a priority. Use ${TICKET_PRIORITIES.join(', ')}.` }
    out.push(p)
  }
  return { ok: true, value: out }
}

function resolveCategories(raw: string[], known: string[]): Resolved<string[]> {
  const out: string[] = []
  for (const v of raw) {
    const k = v.toLowerCase()
    const exact = known.find((c) => c.toLowerCase() === k)
    const partial = exact ? [] : known.filter((c) => c.toLowerCase().includes(k))
    const hit = exact ?? (partial.length === 1 ? partial[0] : undefined)
    if (!hit) {
      return { ok: false, error: partial.length > 1
        ? `"${v}" matches several categories (${partial.join(', ')}). Which one?`
        : `"${v}" is not a ticket category. Known: ${known.join(', ')}.` }
    }
    out.push(hit)
  }
  return { ok: true, value: out }
}

function resolveOne<T extends { id: string; name: string; email?: string }>(v: string, items: T[], kind: string): Resolved<T> {
  const k = v.toLowerCase()
  const exact = items.find((x) => x.id === v || x.name.toLowerCase() === k || x.email?.toLowerCase() === k)
  if (exact) return { ok: true, value: exact }
  const partial = items.filter((x) => x.name.toLowerCase().includes(k))
  if (partial.length === 1) return { ok: true, value: partial[0]! }
  if (partial.length > 1) return { ok: false, error: `"${v}" matches several ${kind}s (${partial.slice(0, 6).map((x) => x.name).join(', ')}). Which one?` }
  return { ok: false, error: `No ${kind} matches "${v}".` }
}

const ME = '__me__'
const NOBODY = '__nobody__'

// Person values: "me", "unassigned" (assignee only), or a user by id, name,
// email or unique part of a name.
function resolvePeople(raw: string[], ctx: QueryContext, allowNobody: boolean): Resolved<{ ids: string[]; label: string }> {
  const ids: string[] = []
  const labels: string[] = []
  for (const v of raw) {
    const k = v.toLowerCase()
    if (k === 'me' || k === 'myself' || k === 'mine') {
      if (!ctx.currentUserId) return { ok: false, error: 'Can’t tell who "me" is: no one is signed in.' }
      ids.push(ME); labels.push('me'); continue
    }
    if (['unassigned', 'nobody', 'none', 'no one', '__unassigned__'].includes(k)) {
      if (!allowNobody) return { ok: false, error: `"${v}" only makes sense for the assignee.` }
      ids.push(NOBODY); labels.push('nobody'); continue
    }
    const r = resolveOne(v, ctx.users, 'user')
    if (!r.ok) return r
    ids.push(r.value.id); labels.push(r.value.name)
  }
  return { ok: true, value: { ids, label: list(labels) } }
}

function personMatcher(ids: string[], ctx: QueryContext): (id: string | null) => boolean {
  const wanted = new Set(ids.map((id) => (id === ME ? ctx.currentUserId : id)))
  return (id) => (id ? wanted.has(id) : wanted.has(NOBODY))
}

function needOp(c: TicketCondition, allowed: QueryOp[]): string | null {
  return allowed.includes(c.op) ? null : `"${c.op}" doesn’t apply to ${c.field}. Use ${allowed.join(' or ')}.`
}

interface Piece { match: (t: Ticket) => boolean; text: string }

function compileCondition(c: TicketCondition, ctx: QueryContext): Resolved<Piece> {
  if (!QUERY_FIELDS.includes(c.field)) return { ok: false, error: `Unknown field "${String(c.field)}". Fields: ${QUERY_FIELDS.join(', ')}.` }
  const vals = values(c)
  if (vals.length === 0 && c.field !== 'overdue') return { ok: false, error: `Give a value for ${c.field}.` }
  const negate = c.op === 'not'

  switch (c.field) {
    case 'status': {
      const bad = needOp(c, ['in', 'not']); if (bad) return { ok: false, error: bad }
      const r = resolveStatuses(vals); if (!r.ok) return r
      const set = new Set(r.value.statuses)
      return { ok: true, value: { match: (t) => set.has(t.status) !== negate, text: negate ? `not ${r.value.label}` : r.value.label } }
    }
    case 'priority': {
      const bad = needOp(c, ['in', 'not']); if (bad) return { ok: false, error: bad }
      const r = resolvePriorities(vals); if (!r.ok) return r
      const set = new Set(r.value)
      return { ok: true, value: { match: (t) => set.has(t.priority) !== negate, text: `${negate ? 'priority not' : 'priority'} ${list(r.value)}` } }
    }
    case 'category': {
      const bad = needOp(c, ['in', 'not']); if (bad) return { ok: false, error: bad }
      const r = resolveCategories(vals, ctx.categories); if (!r.ok) return r
      const set = new Set(r.value.map((x) => x.toLowerCase()))
      return { ok: true, value: { match: (t) => set.has(t.category.toLowerCase()) !== negate, text: `${negate ? 'not in category' : 'in category'} ${list(r.value)}` } }
    }
    case 'customer': {
      const bad = needOp(c, ['in', 'not']); if (bad) return { ok: false, error: bad }
      const found: { id: string; name: string }[] = []
      for (const v of vals) {
        const r = resolveOne(v, ctx.customers, 'customer')
        if (!r.ok) return { ok: false, error: `${r.error} Known customers: ${ctx.customers.map((x) => x.name).join(', ')}.` }
        found.push(r.value)
      }
      const set = new Set(found.map((x) => x.id))
      return { ok: true, value: { match: (t) => (t.orgId ? set.has(t.orgId) : false) !== negate, text: `${negate ? 'not for customer' : 'for customer'} ${list(found.map((x) => x.name))}` } }
    }
    case 'assignee':
    case 'requester':
    case 'createdBy': {
      const bad = needOp(c, ['in', 'not']); if (bad) return { ok: false, error: bad }
      const r = resolvePeople(vals, ctx, c.field === 'assignee'); if (!r.ok) return r
      const is = personMatcher(r.value.ids, ctx)
      const get = (t: Ticket) => (c.field === 'assignee' ? t.assigneeId : c.field === 'requester' ? t.requesterId : t.createdBy)
      const verb = c.field === 'assignee' ? 'assigned to' : c.field === 'requester' ? 'created for' : 'entered by'
      const text = c.field === 'assignee' && r.value.label === 'nobody'
        ? (negate ? 'assigned' : 'unassigned')
        : `${negate ? 'not ' : ''}${verb} ${r.value.label}`
      return { ok: true, value: { match: (t) => is(get(t)) !== negate, text } }
    }
    case 'overdue': {
      const bad = needOp(c, ['is', 'in']); if (bad) return { ok: false, error: bad }
      const want = (vals[0] ?? 'true').toLowerCase() !== 'false'
      const overdue = (t: Ticket) => isOpenStatus(t.status) && !!t.dueDate && (dayOf(t.dueDate) ?? '') < ctx.today
      return { ok: true, value: { match: (t) => overdue(t) === want, text: want ? 'overdue' : 'not overdue' } }
    }
    case 'dueDate':
    case 'createdAt':
    case 'updatedAt': {
      const bad = needOp(c, ['before', 'after']); if (bad) return { ok: false, error: bad }
      const day = vals[0]!
      if (!DATE_RE.test(day)) return { ok: false, error: `Give ${c.field} as YYYY-MM-DD (got "${day}").` }
      const field: 'dueDate' | 'createdAt' | 'updatedAt' = c.field
      const label = field === 'dueDate' ? 'due' : field === 'createdAt' ? 'created' : 'last updated'
      // before: strictly earlier day; after: strictly later day. A ticket
      // with no due date matches neither.
      return { ok: true, value: {
        match: (t) => {
          const d = dayOf(t[field])
          if (!d) return false
          return c.op === 'before' ? d < day : d > day
        },
        text: `${label} ${c.op} ${formatDay(day)}`,
      } }
    }
    case 'text': {
      const bad = needOp(c, ['contains', 'not']); if (bad) return { ok: false, error: bad }
      const needles = vals.map((v) => v.toLowerCase())
      const has = (t: Ticket) => needles.some((n) => `${t.ticketNumber}\n${t.title}\n${t.description}`.toLowerCase().includes(n))
      return { ok: true, value: { match: (t) => has(t) !== negate, text: `${negate ? 'not mentioning' : 'mentioning'} ${list(vals.map((v) => `"${v}"`))}` } }
    }
    case 'ticketNumber': {
      const bad = needOp(c, ['in', 'not']); if (bad) return { ok: false, error: bad }
      const set = new Set(vals.map((v) => v.toLowerCase()))
      return { ok: true, value: { match: (t) => (set.has(t.ticketNumber.toLowerCase()) || set.has(t.id.toLowerCase())) !== negate, text: `${negate ? 'except' : 'ticket'} ${list(vals)}` } }
    }
  }
}

const SORT_LABEL: Record<SortField, string> = {
  ticketNumber: 'ticket number', title: 'title', status: 'status', priority: 'priority', category: 'category',
  assignee: 'assignee', requester: 'created for', dueDate: 'due date', createdAt: 'created date', updatedAt: 'last updated',
}

function compileSort(sort: NonNullable<TicketQuery['sort']>, ctx: QueryContext): Resolved<{ compare: (a: Ticket, b: Ticket) => number; text: string }> {
  if (!SORT_FIELDS.includes(sort.field)) return { ok: false, error: `Can’t sort by "${String(sort.field)}". Sort fields: ${SORT_FIELDS.join(', ')}.` }
  const dir = sort.direction === 'desc' ? -1 : 1
  const name = (id: string | null) => (id ? ctx.users.find((u) => u.id === id)?.name ?? '' : '')
  const key = (t: Ticket): string | number | null => {
    switch (sort.field) {
      case 'priority': return TICKET_PRIORITIES.indexOf(t.priority)
      case 'status': return TICKET_STATUSES.indexOf(t.status)
      case 'assignee': return name(t.assigneeId) || null
      case 'requester': return name(t.requesterId) || null
      case 'dueDate': return t.dueDate
      case 'createdAt': return t.createdAt
      case 'updatedAt': return t.updatedAt
      case 'ticketNumber': return t.ticketNumber
      case 'title': return t.title
      case 'category': return t.category
    }
  }
  const compare = (a: Ticket, b: Ticket) => {
    const ka = key(a)
    const kb = key(b)
    // Blanks (no due date, unassigned) always sort last, either direction.
    if (ka === null || ka === '') return kb === null || kb === '' ? 0 : 1
    if (kb === null || kb === '') return -1
    const r = typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), undefined, { numeric: true })
    return r * dir
  }
  const order = sort.field === 'priority'
    ? (dir === 1 ? 'lowest first' : 'highest first')
    : ['dueDate', 'createdAt', 'updatedAt'].includes(sort.field)
      ? (dir === 1 ? 'oldest first' : 'newest first')
      : (dir === 1 ? 'A to Z' : 'Z to A')
  return { ok: true, value: { compare, text: `sorted by ${SORT_LABEL[sort.field]}, ${order}` } }
}

export function compileTicketQuery(query: TicketQuery, ctx: QueryContext): CompiledQuery {
  const pieces: Piece[] = []
  for (const c of query.where ?? []) {
    const r = compileCondition(c, ctx)
    if (!r.ok) return r
    pieces.push(r.value)
  }
  let compare: ((a: Ticket, b: Ticket) => number) | null = null
  let sortText = ''
  if (query.sort) {
    const r = compileSort(query.sort, ctx)
    if (!r.ok) return r
    compare = r.value.compare
    sortText = r.value.text
  }
  const filterText = pieces.length > 0 ? pieces.map((p) => p.text).join(', ') : 'all tickets'
  return {
    ok: true,
    match: (t) => pieces.every((p) => p.match(t)),
    compare,
    description: sortText ? `${filterText}; ${sortText}` : filterText,
  }
}

export function runTicketQuery(tickets: Ticket[], compiled: Extract<CompiledQuery, { ok: true }>): Ticket[] {
  const rows = tickets.filter(compiled.match)
  return compiled.compare ? rows.sort(compiled.compare) : rows
}

/**
 * Reads a query from tool input or the ?ai= URL param. Accepts the array
 * form ([{field, op, value}]) and also a JSON string of it, since a model
 * sometimes stringifies nested arguments. Returns null for anything that
 * isn't query-shaped, so a hand-edited URL can't crash the grid.
 */
export function parseTicketQuery(raw: unknown): TicketQuery | null {
  let v = raw
  if (typeof v === 'string') {
    try { v = JSON.parse(v) } catch { return null }
  }
  if (Array.isArray(v)) v = { where: v }
  if (!v || typeof v !== 'object') return null
  const obj = v as { where?: unknown; sort?: unknown }
  const where = obj.where === undefined ? [] : obj.where
  if (!Array.isArray(where)) return null
  const conditions: TicketCondition[] = []
  for (const c of where) {
    if (!c || typeof c !== 'object') return null
    const { field, op, value } = c as Record<string, unknown>
    if (typeof field !== 'string') return null
    const opName = typeof op === 'string' && op ? (op === 'equals' ? 'in' : op) : 'in'
    const val = Array.isArray(value) ? value.map(String) : value === undefined || value === null ? '' : String(value)
    conditions.push({ field: field as QueryField, op: opName as QueryOp, value: val })
  }
  let sort: TicketQuery['sort']
  if (obj.sort && typeof obj.sort === 'object') {
    const s = obj.sort as Record<string, unknown>
    if (typeof s.field === 'string') sort = { field: s.field as SortField, direction: s.direction === 'desc' ? 'desc' : 'asc' }
  }
  return sort ? { where: conditions, sort } : { where: conditions }
}
